import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';

import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { enableDebug } from '@sitecore-jss/sitecore-jss-react';
import AppRoot from './AppRoot';
import AOSProvider from './providers/AOSProvider';
import GraphQLClientFactory from './lib/GraphQLClientFactory';
import config from './temp/config';
import i18ninit from './i18n';
import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';
import { initSafePerformanceOptimization } from './utils/safePerformanceOptimizer';

if (process.env.REACT_APP_DEBUG) {
  enableDebug(process.env.REACT_APP_DEBUG);
}

/* eslint-disable no-underscore-dangle */

let renderFunction = (rootElement, component) => {
  const root = createRoot(rootElement);
  root.render(component);
};

let initLanguage = config.defaultLanguage;

/*
  SSR Data
  If we're running in a server-side rendering scenario,
  the server will provide JSON in the #__JSS_STATE__ element
  for us to acquire the initial state to run with on the client.

  This enables us to skip a network request to load up the layout data.
  We are emitting a quiescent script with JSON so that we can take advantage
  of JSON.parse()'s speed advantage over parsing full JS, and enable
  working without needing `unsafe-inline` in Content Security Policies.

  SSR is initiated from /server/server.js.
*/
let __JSS_STATE__ = null;
const ssrRawJson = document.getElementById('__JSS_STATE__');
if (ssrRawJson) {
  __JSS_STATE__ = JSON.parse(ssrRawJson.innerHTML);
}
if (__JSS_STATE__) {
  // when React initializes from a SSR-based initial state, you need to render with `hydrateRoot` instead of `render`
  renderFunction = hydrateRoot;

  // set i18n language SSR state language instead of static config default language
  initLanguage = __JSS_STATE__.sitecore.context.language;
}

/*
  GraphQL Data
  The Apollo Client needs to be initialized to make GraphQL available to the JSS app.
  Not using GraphQL? Remove this, and the ApolloContext from `AppRoot`.
*/
// Apollo supports SSR of GraphQL queries, so like JSS SSR, it has an object we can pre-hydrate the client cache from
// to avoid needing to re-run GraphQL queries after the SSR page loads
const initialGraphQLState =
  __JSS_STATE__ && __JSS_STATE__.APOLLO_STATE ? __JSS_STATE__.APOLLO_STATE : null;

const graphQLClient = GraphQLClientFactory(config.graphQLEndpoint, false, initialGraphQLState);

/*
  App Rendering
*/
// initialize the dictionary, then render the app
// note: if not making a multlingual app, the dictionary init can be removed.

i18ninit(initLanguage).then(() => {
  // HTML element to place the app into
  const rootElement = document.getElementById('root');

  renderFunction(
    rootElement,
    <AOSProvider>
      <AppRoot
        path={window.location.pathname}
        Router={BrowserRouter}
        graphQLClient={graphQLClient}
        ssrState={__JSS_STATE__}
      />
    </AOSProvider>
  );

  // 🚀 性能监控 - 只在开发环境输出到控制台
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    // 定义评级的中文显示
    const getRatingText = (rating) => {
      const ratingMap = {
        good: '✅ 良好',
        'needs-improvement': '⚠️ 需改进',
        poor: '❌ 差',
      };
      return ratingMap[rating] || rating;
    };

    // 定义目标值
    const getTargetText = (name) => {
      const targets = {
        LCP: '目标 <2.5s',
        FCP: '目标 <1.8s',
        CLS: '目标 <0.1',
        INP: '目标 <200ms',
        TTFB: '目标 <0.8s',
      };
      return targets[name] || '';
    };

    // 简单的性能监控函数 - 只输出到控制台
    const logPerformance = (metric) => {
      const { name, value, rating } = metric;
      const formattedValue = name === 'CLS' ? value.toFixed(3) : `${Math.round(value)}ms`;

      console.log(`%c[性能监控] ${name}`, 'color: #2196F3; font-weight: bold; font-size: 14px;', {
        值: formattedValue,
        评级: getRatingText(rating),
        说明: getTargetText(name),
      });
    };

    // 监控 5 个核心 Web Vitals 指标
    onCLS(logPerformance); // 累积布局偏移
    onINP(logPerformance); // 交互响应延迟 (INP 替代了旧的 FID)
    onFCP(logPerformance); // 首次内容绘制
    onLCP(logPerformance); // 最大内容绘制
    onTTFB(logPerformance); // 首字节时间

    console.log(
      '%c🚀 性能监控已启动',
      'background: #4CAF50; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;',
      '\n查看上方的性能指标了解页面加载速度'
    );
  }

  // 在应用渲染后初始化安全的性能优化
  setTimeout(() => {
    try {
      initSafePerformanceOptimization();
    } catch (error) {
      console.warn('性能优化初始化失败:', error);
    }
  }, 100);
});
